/**
 * Generates HTML email template for "Advance Invoice".
 * Sent to clients when an advance or additional services invoice is created.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';
import { trunc2 } from '@/utils';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * @param {Object}  opts
 * @param {string}  opts.clientName
 * @param {number}  opts.itemCost              - subtotal (sum of all service amounts, incl. per-service tax)
 * @param {number}  opts.total                 - grand total due
 * @param {number}  [opts.trustjetFee]         - TrustJet platform fee amount (rendered when NOT oneOff)
 * @param {number}  [opts.trustjetFeePercent]  - percent value used in the label ("TrustJet Fee (1.5%)"). Defaults to 1.5.
 * @param {boolean} [opts.oneOff]              - when true, HIDES the TrustJet Fee row (used by the ad-hoc / one-off invoice flow)
 * @param {string}  [opts.quoteNumber]
 * @param {string}  [opts.checkoutUrl]   - href for the "Settle Invoice" CTA button
 * @param {Array}   [opts.services]      - [{ item, amount, tax }]
 * @param {Object}  [opts.organization]
 */
export const buildInvoiceEmailHtml = ({
  clientName,
  itemCost,
  total,
  quoteNumber,
  trustjetFee,
  trustjetFeePercent = 1.5,
  feeRate,
  taxAmount,
  checkoutUrl,
  services,
  organization = null,
  oneOff = false
}) => {
  // Trim trailing zeros: 1.50 → "1.5", 1.25 → "1.25".
  const feePercentStr = (() => {
    const n = Number(trustjetFeePercent);
    if (!Number.isFinite(n)) return String(trustjetFeePercent ?? '1.5');
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  })();

  // ── Per-service rows ─────────────────────────────────────────────────────
  // Each service renders as a gray-background block (sub-table) with a white
  // gap between groups, matching the invoice details mockup layout.
  const serviceRowsHtml = (() => {
    if (services?.length > 0) {
      return services.map((s, i) => {
        const isLast = i === services.length - 1;
        return `
              <tr>
                <td colspan="2" style="padding:0 20px ${isLast ? '0' : '6px'} 20px;">
                  <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="padding:12px 20px 4px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">${s.item || s.name || ''}</td>
                      <td align="right" style="padding:12px 20px 4px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">$${fmt(s.amount)}</td>
                    </tr>
                  </table>
                </td>
              </tr>`;
      }).join('');
    }
    return '';
  })();

  // ── Settle Invoice button ─────────────────────────────────────────────────
  const settleButtonHtml = checkoutUrl
    ? `<a href="${checkoutUrl}" target="_blank" style="display:inline-block;margin-top:20px;padding:12px 28px;background-color:#1B3A5C;color:#FFFFFF;font-size:14px;font-weight:600;text-decoration:none;border-radius:100px;">Settle Invoice</a>`
    : '';

  return `
${emailDocOpen({ title: oneOff ? 'Advance Invoice for Review' : 'Invoice For Review' })}
${emailHeader({ title: oneOff ? 'Advance Invoice for Review' : 'Invoice For Review' })}

        ${spacer()}

        <!-- ===== CLIENT / REFERENCE ROW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice For</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${clientName || 'Valued Client'}</span>
                  </td>
                  ${quoteNumber ? `<td align="right">
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">TR Number</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${quoteNumber}</span>
                  </td>` : ''}
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== NOTIFICATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 28px 32px;">
              <!-- Icon -->
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
                <tr>
                  <td style="width:48px;height:48px;text-align:center;vertical-align:middle;background-color:#FEF3EB;border-radius:100%;">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="#FA7319" style="display:block;margin:0 auto;">
                      <path d="M21 15.2426V21.0082C21 21.556 20.5551 22 20.0066 22H3.9934C3.44476 22 3 21.5511 3 20.9925V9H9C9.55228 9 10 8.55228 10 8V2H20.0017C20.5531 2 21 2.45531 21 2.9918V6.75736L12.0012 15.7562L11.995 19.995L16.2414 20.0012L21 15.2426ZM21.7782 8.80761L23.1924 10.2218L15.4142 18L13.9979 17.9979L14 16.5858L21.7782 8.80761ZM3 7L8 2.00318V7H3Z"/>
                    </svg>
                  </td>
                </tr>
              </table>
              <!-- Body text -->
              <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
              ${oneOff ? `
                You have received an advance invoice from <strong style="color:#111827;">${organization?.name || ''}</strong>. This is a general invoice not related to a specific trip. Please review the invoice details below.` : `You just received an additional invoice related to your existing trip with Trip ID
                <strong style="color:#111827;">${quoteNumber || ''}</strong>.
                Kindly review and settle outstanding charges below.`}
              </p>
              ${settleButtonHtml}
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== INVOICE DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <!-- Section label -->
          <tr>
            <td colspan="2" style="padding:14px 20px 8px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice Details</span>
            </td>
          </tr>

          <!-- Service rows -->
          ${serviceRowsHtml}

          <!-- Subtotal -->
          <tr>
            <td style="padding:10px 20px 4px;font-size:13px;color:#6B7280;">Subtotal Cost</td>
            <td align="right" style="padding:10px 20px 4px;font-size:13px;color:#6B7280;">$${fmt(itemCost)}</td>
          </tr>

          <!-- Taxes and Fees -->
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Taxes and Fees</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${fmt(trunc2(Number(taxAmount || 0) + Number(trustjetFee || 0)))}</td>
          </tr>

          <!-- Divider before total -->
          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <!-- Total Due -->
          <tr>
            <td style="padding:14px 20px;font-size:15px;font-weight:700;color:#111827;">Total Due</td>
            <td align="right" style="padding:14px 20px;font-size:18px;font-weight:700;color:#111827;">$${fmt(total)}</td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== ASSISTANCE MESSAGE ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;">
          <tr>
            <td style="padding:0 4px;font-size:13px;color:#6B7280;line-height:1.6;">
              For further assistance, please reach out to <strong style="color:#111827;">${organization?.name || 'your broker'}</strong> or contact your broker directly.
            </td>
          </tr>
        </table>

        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
